import { StartOutlined } from '@mui/icons-material'
import React from 'react'
import { useHistory } from 'react-router-dom'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import Refresh from '@mui/icons-material/Refresh'
import { Box, Button, Typography, Paper, Tooltip, Grid, Zoom, Switch } from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridValueGetterParams } from '@mui/x-data-grid'
import HomeIcon from '@mui/icons-material/Home'
import {
  useGetOpenDisbursementBatchesQuery, useGetPendingDisbursementInstructionsQuery
} from '../../../../api-services/payments/paymentsApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import CreateDisbursementBatch from './CreateDisbursementBatch'
import DisbursementInstructionStateChip from './DisbursementInstructionStateChip'
import DisbursementBatchStateChip from './DisbursementBatchStateChip'
import { selectShowAllBatches, setShowAllBatches } from './disbursementsSlice'

const DisbursementBatchesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const showAllBatches: boolean = useAppSelector<boolean>(selectShowAllBatches)
  const getOpenBatchesApiResult = useGetOpenDisbursementBatchesQuery(showAllBatches)
  const getPendingInstructionsApiResult = useGetPendingDisbursementInstructionsQuery({ })
  const refetchBatches = getOpenBatchesApiResult.refetch
  const refetchInstructions = getPendingInstructionsApiResult.refetch

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleRefetch = async (): Promise<void> => {
    await refetchBatches()
    await refetchInstructions()
  }

  const handleGoToManage = (disbursementBatchId: number | undefined): void => {
    goTo(`/app/finance-area/disbursements/batches/${disbursementBatchId}`)
  }

  const handleBatchFilterSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      dispatch(setShowAllBatches(true))
    } else {
      dispatch(setShowAllBatches(false))
    }
  }

  const batchColumns: GridColDef[] = [
    { field: 'disbursementBatchId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      flex: 35,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.currencyCode} name={`[${params.row.currencyCode}] ${params.row.currencyCode}`}/>
    },
    { field: 'schedule', headerName: 'Schedule', flex: 40, editable: false },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <DisbursementBatchStateChip batchState={params.row.state}/>
    },
    { field: 'countInstructions', headerName: 'Count', flex: 50, editable: false },
    { field: 'totalRequestedAmountFormatted', headerName: 'Requested Amount', flex: 50, editable: false, align: 'right' },
    { field: 'totalFeeAmountFormatted', headerName: 'Fee Amount', flex: 50, editable: false, align: 'right' },
    { field: 'totalPayoutAmountFormatted', headerName: 'Payout Amount', flex: 50, editable: false, align: 'right' },
    { field: 'exportedAtFormatted', headerName: 'Exported At', flex: 50, editable: false },
    { field: 'exportedByName', headerName: 'Exported By', flex: 50, editable: false },
    {
      field: 'manageDisbursementBatch', // Don't repeat ID - does weird things with the ordering of the columns
      headerName: '',
      width: 100,
      valueGetter: (params: GridValueGetterParams): number => params.row.disbursementBatchId,
      renderCell: (params: GridRenderCellParams<number>) => (
        <Button onClick={() => { handleGoToManage(params.value) }} type={'button'} variant={'text'}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  const instructionColumns: GridColDef[] = [
    { field: 'paymentInstructionId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      flex: 35,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.currencyCode} name={`[${params.row.srcCurrcurrencyCodeencyId}] ${params.row.currencyCode}`}/>
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <DisbursementInstructionStateChip batchState={params.row.state}/>
    },
    { field: 'schedule', headerName: 'Schedule', flex: 40, editable: false },
    { field: 'assetHolderId', headerName: 'Member ID', flex: 50, editable: false },
    { field: 'assetHolderEmailAddress', headerName: 'Email address', flex: 80, editable: false },
    { field: 'requestAmountFormatted', headerName: 'Requested Amount', flex: 50, editable: false, align: 'right' },
    { field: 'feeAmountFormatted', headerName: 'Fee Amount', flex: 50, editable: false, align: 'right' },
    { field: 'payoutAmountFormatted', headerName: 'Payout Amount', flex: 50, editable: false, align: 'right' },
    { field: 'confirmedAtFormatted', headerName: 'Confirmed At', flex: 50, editable: false },
    { field: 'accountAddressInfo', headerName: 'Recipient', flex: 50, editable: false },
    {
      field: 'disbursementAccountId',
      headerName: 'Disbursement Account',
      flex: 50,
      editable: false,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
            <Tooltip title={JSON.stringify({ ...params.row.accountDetails, disbursementAccountId: params.row.disbursementAccountId }, null, 2)} TransitionComponent={Zoom} arrow placement={'left'}>
              <InfoIcon />
            </Tooltip>
        )
      }
    }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Disbursements</Typography>
        <Button variant={'text'} type="button" onClick={() => { goTo('/app/finance-area') }}>
          <HomeIcon style={{ color: '#444' }} />
        </Button>
      </Box>

      <Paper>
        <Grid container justifyContent="right" sx={{ pr: 4, pt: 2 }}>
          <Grid item>
            <Button variant={'contained'} type="button" onClick={handleRefetch}>
              <Refresh/>
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
          <h3>Open Disbursement Batches</h3>
          { getOpenBatchesApiResult.isLoading && <LoadingComponent />}
          { getOpenBatchesApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getOpenBatchesApiResult.error)} />}
          { getOpenBatchesApiResult.isSuccess && (
            <DataGrid
              getRowId={(row) => row.disbursementBatchId}
              rows={getOpenBatchesApiResult.data}
              columns={batchColumns}
              autoHeight={true}

              pagination
              pageSize={5}
              rowsPerPageOptions={[5]}
            />
          )}
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
          Show All Batches
          <Switch
            checked={showAllBatches}
            onChange={handleBatchFilterSwitch}
            color="primary"
          />
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
          <h3>Create Disbursement Batch</h3>
          { getPendingInstructionsApiResult.isLoading && <LoadingComponent />}
          { getPendingInstructionsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPendingInstructionsApiResult.error)} />}
          { getPendingInstructionsApiResult.isSuccess && (
            <CreateDisbursementBatch pendingInstructions={getPendingInstructionsApiResult.data} />
          )}
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
          <h3>Pending Disbursement Instructions</h3>
          { getPendingInstructionsApiResult.isLoading && <LoadingComponent />}
          { getPendingInstructionsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPendingInstructionsApiResult.error)} />}
          { getPendingInstructionsApiResult.isSuccess && (
            <DataGrid
              getRowId={(row) => row.paymentInstructionId}
              rows={getPendingInstructionsApiResult.data}
              columns={instructionColumns}
              autoHeight={true}
              disableSelectionOnClick

              pagination
              pageSize={15}
              rowsPerPageOptions={[15]}
            />
          )}
        </Box>
      </Paper>
    </>
  )
}

export default DisbursementBatchesPage
